import { findFirst } from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import { getOrElse } from 'fp-ts/Option';
import { keys } from 'fp-ts/Record';
import { useState } from 'react';
import { ReservationState, ReservationSummary } from '@mews-commander/guest-profile';
import { lt } from '@mews/framework';
import { Container, Divider, SegmentedControl, SegmentedControlItem } from '@optimus-web/core';
import { useGetReservationsSections } from '../hooks';
import { ReservationItem } from './ReservationItem';

const labels = {
    [ReservationState.Active]: lt('Active'),
    [ReservationState.Canceled]: lt('Canceled'),
    [ReservationState.Past]: lt('Past'),
};

type SectionProps = {
    [key in ReservationState]: ReservationSummary[];
};

const isSection = (value: string): value is ReservationState => Object.values<string>(ReservationState).includes(value);

const getFirstNonEmptySection =
    (sections: SectionProps) =>
    (defaultState: ReservationState): ReservationState =>
        pipe(
            sections,
            keys,
            findFirst((key) => Array.isArray(sections[key]) && sections[key].length > 0),
            getOrElse(() => defaultState)
        );

export interface ReservationsSegmentControlProps {
    reservations: Array<ReservationSummary>;
}

export const ReservationsSegmentControl = ({ reservations }: ReservationsSegmentControlProps) => {
    const sections = useGetReservationsSections(reservations);

    const [selectedSection, setSelectedSection] = useState(getFirstNonEmptySection(sections)(ReservationState.Active));

    const getItemProps = (section: ReservationState) => ({
        label: labels[section],
        value: section,
        disabled: !sections[section].length,
    });

    return (
        <>
            <Container padding={['0', '200', '200']}>
                <SegmentedControl
                    value={selectedSection}
                    onChange={(_, value) => {
                        if (isSection(value)) setSelectedSection(value);
                    }}
                    size="small"
                >
                    <SegmentedControlItem {...getItemProps(ReservationState.Active)} />
                    <SegmentedControlItem {...getItemProps(ReservationState.Past)} />
                    <SegmentedControlItem {...getItemProps(ReservationState.Canceled)} />
                </SegmentedControl>
            </Container>
            <Divider />
            {selectedSection && sections[selectedSection].length ? (
                <Container padding={['0', '200', '0']}>
                    {sections[selectedSection].map((reservation, index) => (
                        <ReservationItem
                            key={reservation.id}
                            {...reservation}
                            lastItem={index === sections[selectedSection].length - 1}
                        />
                    ))}
                </Container>
            ) : null}
        </>
    );
};
